var _ref, _ref$GLOBAL_STATE_KEY;
import { context, trace, TraceFlags, SpanStatusCode, propagation } from '@opentelemetry/api';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ExportResultCode, W3CTraceContextPropagator, CompositePropagator } from '@opentelemetry/core';
import { BatchSpanProcessor, ParentBasedSampler, SimpleSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider, StackContextManager } from '@opentelemetry/sdk-trace-web';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import Raven from 'raven-js';
import enviro from 'enviro';
import { getFullUrl } from 'hubspot-url-utils';
import { na1 } from 'hubspot-url-utils/hublets';
import { OtlpExporter, LOCAL_OTLP_ENDPOINT } from './otlpExporter';
import { HsTraceIdRatioBasedSampler } from './HsTraceIdRatioBasedSampler';
import { getElementSelector, getNearestComponentName } from './elementSelector';
import { instrumentQuickFetch } from '../integrations/quickfetch';
import { WebSocketInstrumentation } from '../integrations/websocket';
import { instrumentSpaNavigation } from '../quartz/routing';
import { DevConsoleSpanExporter } from './DevConsoleSpanExporter';
const LOCAL_OTLP_FLAG = 'OTEL_LOCAL_EXPORTER';
const PUBLIC_TELEMETRY_PATH = '/hs-open-telemetry-public/v1/public';

// https://philipwalton.com/articles/idle-until-urgent/
const isSafari = () => !!(typeof window.safari === 'object' && window.safari.pushNotification);
function getPublicBaseUrl(hublet) {
  const apiUrl = getFullUrl('api');
  // getFullUrl('api') returns 'https://api.hubspot.com' for na1 but
  // 'https://api-eu1.hubspot.com' for other hublets, so use a regex
  // that strips the entire 'api(-hublet)?' subdomain.
  return apiUrl.replace(/\/\/api(?:-[^.]+)?\./, `//metrics-fe-${hublet}.`);
}
async function fetchPublicSampleRate(serviceName) {
  var _enviro$getHublet, _data$sampleRatio;
  const url = `${getPublicBaseUrl(na1)}${PUBLIC_TELEMETRY_PATH}/sample-ratios?serviceName=${encodeURIComponent(serviceName)}&hublet=${encodeURIComponent(enviro.deployed() ? (_enviro$getHublet = enviro.getHublet()) !== null && _enviro$getHublet !== void 0 ? _enviro$getHublet : na1 : na1)}`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const data = await res.json();
  return (_data$sampleRatio = data.sampleRatio) !== null && _data$sampleRatio !== void 0 ? _data$sampleRatio : null;
}
class NoopSpanExporter {
  export(_spans, resultCallback) {
    resultCallback({
      code: ExportResultCode.SUCCESS
    });
  }
  shutdown() {
    return Promise.resolve();
  }
}
const GLOBAL_STATE_KEY = '__otelFe_1';
const _state = (_ref$GLOBAL_STATE_KEY = (_ref = window)[GLOBAL_STATE_KEY]) !== null && _ref$GLOBAL_STATE_KEY !== void 0 ? _ref$GLOBAL_STATE_KEY : _ref[GLOBAL_STATE_KEY] = {
  provider: null,
  initializing: false,
  serviceName: null,
  isSampled: null,
  exportResults: [],
  exportFailureReported: false,
  unloadListener: null,
  listenTo: null,
  wsInstrumentation: null,
  spaNavUnlisten: null
};
let _testExporter = null;
const _readyCallbacks = [];
export async function initTracing(serviceName, {
  enabled,
  sampleRate = 0.01,
  dynamicSampleRate = true,
  instrumentDocumentLoad = true,
  instrumentFetch = false,
  instrumentUserInteraction = true,
  userInteractionElementSelector,
  instrumentXhr = false,
  instrumentQuickFetch: instrumentQuickFetchOption = false,
  instrumentWebSocket = false,
  instrumentSpaNavigation: instrumentSpaNavigationOption = false,
  propagateTraceHeaderCorsUrls,
  propagateTraceHeaderWebSocketUrls,
  ignoreUrls,
  applyCustomAttributesOnSpan,
  httpClient,
  endpoint,
  additionalSpanProcessors
} = {}) {
  if (_state.provider !== null || _state.initializing) {
    console.warn(`[opentelemetry-fe] initTracing() was already called. Ignoring duplicate call with serviceName "${serviceName}".`);
    return;
  }
  _state.initializing = true;
  if (instrumentQuickFetchOption) {
    instrumentQuickFetch();
  }
  try {
    var _hubspot;
    const isEnabled = enabled !== null && enabled !== void 0 ? enabled : true;
    if (dynamicSampleRate && isEnabled) {
      try {
        const fetched = await fetchPublicSampleRate(serviceName);
        if (fetched !== null) {
          sampleRate = fetched;
        } else {
          console.warn(`[opentelemetry-fe] No sample ratio returned for "${serviceName}"; using default ${sampleRate}.`);
        }
      } catch (err) {
        console.warn(`[opentelemetry-fe] Failed to fetch sample rate for "${serviceName}"; using default ${sampleRate}. Error: ${String(err)}`);
      }
    }
    if (applyCustomAttributesOnSpan && !instrumentXhr) {
      console.warn('[opentelemetry-fe] applyCustomAttributesOnSpan was provided but instrumentXhr is not enabled; the hook will not be called.');
    }
    let exporter;
    let exporterUrl = null;
    if (!isEnabled) {
      exporter = new NoopSpanExporter();
    } else {
      let flag = null;
      try {
        flag = localStorage.getItem(LOCAL_OTLP_FLAG);
      } catch (_e) {
        // localStorage may be unavailable (e.g. private browsing with strict settings)
      }
      if (flag === 'otlp') {
        exporterUrl = LOCAL_OTLP_ENDPOINT;
        exporter = new OtlpExporter();
      } else if (flag === 'tempo' || enviro.deployed()) {
        var _enviro$getHublet2;
        const resolvedEndpoint = endpoint !== null && endpoint !== void 0 ? endpoint : `${getPublicBaseUrl(enviro.deployed() ? (_enviro$getHublet2 = enviro.getHublet()) !== null && _enviro$getHublet2 !== void 0 ? _enviro$getHublet2 : na1 : na1)}${PUBLIC_TELEMETRY_PATH}/traces`;
        exporterUrl = resolvedEndpoint;
        exporter = new OtlpExporter(resolvedEndpoint, {
          httpClient
        });
      } else if (flag === 'console') {
        exporter = new DevConsoleSpanExporter();
      } else {
        if (flag !== null) {
          console.warn(`[opentelemetry-fe] Unrecognized OTEL_LOCAL_EXPORTER value: "${flag}". No spans will be exported.`);
        }
        exporter = new NoopSpanExporter();
      }
    }
    const allIgnoreUrls = [...(exporterUrl ? [new RegExp(exporterUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))] : []), ...(ignoreUrls !== null && ignoreUrls !== void 0 ? ignoreUrls : [])];
    const trackedExporter = {
      export(spans, resultCallback) {
        exporter.export(spans, result => {
          _state.exportResults.push(result);
          if (result.code === ExportResultCode.FAILED && !_state.exportFailureReported) {
            var _result$error;
            _state.exportFailureReported = true;
            Raven.captureMessage('[opentelemetry-fe] Span export failed', {
              level: 'info',
              extra: {
                error: (_result$error = result.error) === null || _result$error === void 0 ? void 0 : _result$error.message
              },
              tags: {
                library_name: 'opentelemetry-fe'
              }
            });
          }
          resultCallback(result);
        });
      },
      shutdown() {
        return exporter.shutdown();
      }
    };
    const spanProcessor = _testExporter ? new SimpleSpanProcessor(_testExporter) : new BatchSpanProcessor(trackedExporter);
    const validatedSampleRate = (() => {
      if (Number.isNaN(sampleRate)) {
        console.warn('[opentelemetry-fe] sampleRate is NaN; falling back to default (0.01).');
        return 0.01;
      }
      if (sampleRate < 0 || sampleRate > 1) {
        const clamped = Math.min(1, Math.max(0, sampleRate));
        console.warn(`[opentelemetry-fe] sampleRate ${sampleRate} is out of range [0.0, 1.0]; clamping to ${clamped}.`);
        return clamped;
      }
      return sampleRate;
    })();
    const sampler = new ParentBasedSampler({
      root: new HsTraceIdRatioBasedSampler(validatedSampleRate)
    });
    const sessionId = (_hubspot = window.hubspot) === null || _hubspot === void 0 ? void 0 : _hubspot.sessionId;
    const provider = new WebTracerProvider({
      resource: resourceFromAttributes(Object.assign({
        [ATTR_SERVICE_NAME]: serviceName
      }, sessionId != null && {
        'hubspot.session_id': String(sessionId)
      })),
      sampler,
      spanProcessors: [spanProcessor, ...(additionalSpanProcessors !== null && additionalSpanProcessors !== void 0 ? additionalSpanProcessors : [])]
    });
    const contextManager = new StackContextManager();
    contextManager.enable();
    const shouldInstrument = instrumentDocumentLoad || instrumentFetch || instrumentUserInteraction || instrumentXhr || instrumentWebSocket;
    provider.register({
      contextManager,
      propagator: isEnabled ? new W3CTraceContextPropagator() : new CompositePropagator()
    });
    if (shouldInstrument) {
      const instrumentations = [];
      if (instrumentDocumentLoad) {
        instrumentations.push(new DocumentLoadInstrumentation());
      }
      if (instrumentFetch) {
        instrumentations.push(new FetchInstrumentation({
          propagateTraceHeaderCorsUrls,
          ignoreUrls: allIgnoreUrls
        }));
      }
      if (instrumentUserInteraction) {
        // Workaround for a bug in @opentelemetry/instrumentation-user-interaction@0.65.0:
        // addPatchedListener stores event targets and listeners as WeakMap keys without guarding
        // against non-object values, causing "Invalid value used as weak map key" crashes.
        // A bare addEventListener(...) call (no `window.`) reaches the patched function with
        // `this` undefined, which the native method treats as the global object, so we key
        // those registrations on window. Returning false would make the patched function skip
        // the native addEventListener and silently drop the listener.
        // The methods are typed private so we can't subclass — patch the instance directly.
        // Remove this patch once upstream ships a fix.
        const uiInstr = new UserInteractionInstrumentation({
          shouldPreventSpanCreation: (_eventType, element, span) => {
            const selectorFn = userInteractionElementSelector !== null && userInteractionElementSelector !== void 0 ? userInteractionElementSelector : getElementSelector;
            const selector = selectorFn(element);
            if (selector) {
              span.setAttribute('ui.element.selector', selector);
            }
            const componentName = getNearestComponentName(element);
            if (componentName) {
              span.setAttribute('ui.component.name', componentName);
            }
          }
        });
        const origAddPatchedListener = uiInstr.addPatchedListener.bind(uiInstr);
        const origRemovePatchedListener = uiInstr.removePatchedListener.bind(uiInstr);
        const resolveTarget = on => on !== null && on !== void 0 ? on : window;
        uiInstr.addPatchedListener = (on, type, listener, wrappedListener) => {
          const target = resolveTarget(on);
          if (typeof target !== 'object' && typeof target !== 'function') {
            return false;
          }
          if (listener == null || typeof listener !== 'object' && typeof listener !== 'function') {
            return false;
          }
          return origAddPatchedListener(target, type, listener, wrappedListener);
        };
        uiInstr.removePatchedListener = (on, type, listener) => origRemovePatchedListener(resolveTarget(on), type, listener);
        instrumentations.push(uiInstr);
      }
      if (instrumentXhr) {
        instrumentations.push(new XMLHttpRequestInstrumentation({
          propagateTraceHeaderCorsUrls,
          ignoreUrls: allIgnoreUrls,
          applyCustomAttributesOnSpan
        }));
      }
      if (instrumentWebSocket) {
        const wsInstr = new WebSocketInstrumentation({
          ignoreUrls: allIgnoreUrls,
          propagateTraceHeaderWebSocketUrls
        });
        _state.wsInstrumentation = wsInstr;
        instrumentations.push(wsInstr);
      }
      registerInstrumentations({
        instrumentations,
        tracerProvider: provider
      });
    }
    const probeSpan = trace.getTracer(serviceName).startSpan('otel-init');
    _state.isSampled = probeSpan.spanContext().traceFlags === TraceFlags.SAMPLED;
    probeSpan.end();
    _state.provider = provider;
    _state.serviceName = serviceName;
    if (instrumentSpaNavigationOption) {
      _state.spaNavUnlisten = instrumentSpaNavigation();
    }
    for (const cb of _readyCallbacks.splice(0)) {
      cb();
    }
    const listenTo = isSafari() ? 'beforeunload' : 'visibilitychange';
    const flushOnUnload = () => {
      if (document.visibilityState === 'hidden' || listenTo === 'beforeunload') {
        // The browser cancels the in-flight export request when the page is
        // hidden or unloaded, so forceFlush() routinely rejects here (with an
        // array of errors, per BasicTracerProvider). Catch so it does not
        // surface as an unhandled promise rejection.
        provider.forceFlush().catch(e => {
          Raven.captureException(e);
        });
        if (listenTo === 'beforeunload') {
          try {
            window.removeEventListener(listenTo, flushOnUnload, true);
          } catch (_e) {
            // ignore
          }
          _state.unloadListener = null;
        }
      }
    };
    _state.listenTo = listenTo;
    _state.unloadListener = flushOnUnload;
    try {
      window.addEventListener(listenTo, flushOnUnload, true);
    } catch (_e) {
      // ignore
    }
  } finally {
    _state.initializing = false;
  }
}
export function isSampled() {
  return _state.isSampled;
}
export function isTracingInitializing() {
  return _state.initializing;
}
export function onTracingReady(callback) {
  if (_state.provider !== null) {
    callback();
  } else {
    _readyCallbacks.push(callback);
  }
}
export function addTracingSpanProcessor(processor) {
  const doAdd = () => {
    var _multi$_spanProcessor;
    if (_state.provider === null) return;
    // opentelemetry-web/static-1.8 does not expose addSpanProcessor publicly;
    // access the internal MultiSpanProcessor._spanProcessors array directly.
    const multi = _state.provider._activeSpanProcessor;
    multi === null || multi === void 0 || (_multi$_spanProcessor = multi._spanProcessors) === null || _multi$_spanProcessor === void 0 || _multi$_spanProcessor.push(processor);
  };
  if (_state.provider !== null) {
    doAdd();
  } else {
    _readyCallbacks.push(doAdd);
  }
}
export function getTracer(name) {
  if (_state.provider === null && !_state.initializing) {
    throw new Error('OpenTelemetry tracing has not been initialized. Call initTracing(serviceName) before using getTracer(). Use getSafeTracer() if you cannot guarantee initTracing() has been called.');
  }
  return getSafeTracer(name);
}

/**
 * Like getTracer(), but never throws. Returns a no-op tracer if tracing has
 * not been initialized (or is still initializing) instead of raising an
 * error. Intended for library code that emits spans but can't control or
 * verify whether the host app has called initTracing().
 */
export function getSafeTracer(name) {
  if (_state.provider !== null && _state.serviceName !== null) {
    return trace.getTracer(name !== null && name !== void 0 ? name : _state.serviceName);
  }
  // Tracing is either still initializing (e.g. awaiting the dynamic
  // sample-rate fetch) or was never initialized. Either way, no global OTel
  // provider is registered, so trace.getTracer() returns a no-op tracer —
  // spans created from it are silently dropped rather than throwing.
  return trace.getTracer(name !== null && name !== void 0 ? name : 'opentelemetry-fe-pending');
}
export async function flushTracing() {
  var _failed$error;
  if (_state.provider === null) {
    throw new Error('OpenTelemetry tracing has not been initialized. Call initTracing(serviceName) before using flushTracing().');
  }
  _state.exportResults = [];
  try {
    await _state.provider.forceFlush();
  } catch (e) {
    Raven.captureException(e);
  }
  if (_state.exportResults.length === 0) {
    return {
      success: false,
      error: 'No spans were exported'
    };
  }
  const failed = _state.exportResults.find(r => r.code === ExportResultCode.FAILED);
  return {
    success: !failed,
    error: failed === null || failed === void 0 || (_failed$error = failed.error) === null || _failed$error === void 0 ? void 0 : _failed$error.message
  };
}
export async function _initTracingForTesting(serviceName, options, exporter) {
  _testExporter = exporter;
  await initTracing(serviceName, options);
}
export function _setTestExporterForTesting(exporter) {
  _testExporter = exporter;
}
export function _resetForTesting() {
  if (_state.spaNavUnlisten !== null) {
    _state.spaNavUnlisten();
    _state.spaNavUnlisten = null;
  }
  if (_state.wsInstrumentation !== null) {
    _state.wsInstrumentation.disable();
    _state.wsInstrumentation = null;
  }
  if (_state.provider !== null) {
    _state.provider.shutdown().catch(() => {});
  }
  if (_state.unloadListener !== null && _state.listenTo !== null) {
    try {
      window.removeEventListener(_state.listenTo, _state.unloadListener, true);
    } catch (_e) {
      // ignore
    }
  }
  propagation.disable();
  context.disable();
  trace.disable();
  delete window[GLOBAL_STATE_KEY];
  _state.provider = null;
  _state.initializing = false;
  _state.serviceName = null;
  _state.isSampled = null;
  _state.spaNavUnlisten = null;
  _state.exportResults = [];
  _state.exportFailureReported = false;
  _state.unloadListener = null;
  _state.listenTo = null;
  _testExporter = null;
  _readyCallbacks.length = 0;
}
export { context, trace, propagation, SpanStatusCode };